import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fruitBaskets,
  getProduct,
  images,
  initialAddresses,
  products,
  serviceAreas,
  shakeProducts,
  timeSlots,
  weekdays,
} from "../data";
import { useApp } from "../store";
import {
  AddressCard,
  Brand,
  DeliveryCard,
  EmptyState,
  PageHeader,
  PrimaryButton,
  ProductCard,
  SectionTitle,
  StatusBadge,
} from "../components/UI";

import "../css/Home.css";
import CustomerCount from "../components/CustomerCount";
export function Home() {
  const { deliveries, addresses } = useApp();
  const address = addresses.find((item) => item.default) || addresses[0];
  const locationLabel = address
    ? `${address.area || address.city || "Saved address"}, ${address.city || "Pune"}`
    : "Add your address";
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <section className="home-hero">
        <div className="container-xl">
          <div className="mobile-location d-lg-none">
            <i className="fa-solid fa-location-dot" />
            <span>
              <small>Delivering to</small>
              <strong>{locationLabel}</strong>
            </span>
            {/* <i className="fa-solid fa-chevron-down" /> */}
          </div>
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <span className="hero-label">
                <i className="fa-solid fa-leaf" /> Fresh schedules for busy Pune
              </span>
              <h2>
                Schedule it.
                <br />
                <em>We bring it fresh.</em>
              </h2>
              <p>
                Your natural protein shakes and market-fresh fruits, prepared
                around your routine—not sitting around in storage.
              </p>
              <div className="hero-actions">
                <PrimaryButton to="/protein-shakes" className="btn-lg">
                  <i className="fa-solid fa-glass-water me-2" />
                  Schedule a shake
                </PrimaryButton>
                <Link to="/fruits" className="btn btn-light btn-lg">
                  <i className="fa-solid fa-apple-whole me-2" />
                  Schedule fruits
                </Link>
              </div>
              <div className="trust-row">
                <span>
                  <i className="fa-solid fa-circle-check" /> No protein powder
                </span>
                <span>
                  <i className="fa-solid fa-circle-check" /> Pause anytime
                </span>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-visual">
                <img src={images.banana} alt="Fresh banana nutrition shake" />
                <div className="floating-card top">
                  <i className="fa-regular fa-clock" />
                  <span>
                    <small>Your next delivery</small>
                    <strong>
                      {deliveries[0]?.timeSlot || "Your scheduled window"}
                    </strong>
                  </span>
                </div>
                <div className="floating-card bottom">
                  <i className="fa-solid fa-basket-shopping" />
                  <span>
                    <small>Freshness model</small>
                    <strong>Buy → pack → deliver</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-space">
        <div className="container-xl">
          <SectionTitle
            eyebrow="Choose your routine"
            title="Fresh essentials, on repeat."
            copy="Tell us once what you need and when. We’ll handle the rest."
          />
          <div className="row g-4">
            <div className="col-md-6">
              <Link className="service-card shake" to="/protein-shakes">
                <div>
                  <span className="service-icon">
                    <i className="fa-solid fa-glass-water" />
                  </span>
                  <p className="card-kicker">From ₹89</p>
                  <h3>Fresh Protein Shakes</h3>
                  <p>
                    Natural ingredients. Made fresh for your scheduled window.
                  </p>
                  <span className="service-link">
                    Explore shakes <i className="fa-solid fa-arrow-right" />
                  </span>
                </div>
                <img src={images.beetroot_juice} alt="" />
              </Link>
            </div>
            <div className="col-md-6">
              <Link className="service-card fruits" to="/fruits">
                <div>
                  <span className="service-icon">
                    <i className="fa-solid fa-apple-whole" />
                  </span>
                  <p className="card-kicker">From ₹199</p>
                  <h3>Fresh Fruit Baskets</h3>
                  <p>Bought for upcoming demand and delivered to your door.</p>
                  <span className="service-link">
                    Schedule fruits <i className="fa-solid fa-arrow-right" />
                  </span>
                </div>
                <img src={images.fruit} alt="" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-space pt-0">
        <div className="container-xl">
          <SectionTitle
            eyebrow="Coming up"
            title="Your next delivery"
            action="View all"
            to="/deliveries"
          />
          {deliveries.length ? (
            <div className="next-delivery">
              <div className="date-tile">
                <strong>
                  {deliveries[0]?.deliveryDate
                    ? new Date(
                        `${deliveries[0].deliveryDate}T00:00:00`,
                      ).getDate()
                    : "—"}
                </strong>
                <span>
                  {deliveries[0]?.deliveryDate
                    ? new Date(`${deliveries[0].deliveryDate}T00:00:00`)
                        .toLocaleDateString("en-IN", { month: "short" })
                        .toUpperCase()
                    : ""}
                </span>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex gap-2 mb-2">
                  <StatusBadge status="Scheduled" />
                  <span className="small fw-semibold text-secondary">
                    Recurring
                  </span>
                </div>
                <h3>{deliveries[0]?.productName || "Your next delivery"}</h3>
                <p>
                  <i className="fa-regular fa-clock" />{" "}
                  {deliveries[0]?.deliveryDate || "Scheduled"} ·{" "}
                  {deliveries[0]?.timeSlot || "Time window not set"}{" "}
                  <span>·</span> <i className="fa-solid fa-location-dot" />{" "}
                  {locationLabel}
                </p>
              </div>
              <Link
                to={`/deliveries/${deliveries[0]?.id || ""}`}
                className="btn btn-soft"
              >
                View details
              </Link>
            </div>
          ) : (
            <EmptyState
              title="Nothing scheduled yet"
              copy="Start with a fresh protein shake or fruit delivery."
            />
          )}
        </div>
      </section>

      <section className="how-section">
        <div className="container-xl">
          <SectionTitle
            eyebrow="Simple by design"
            title="You schedule. We take care of fresh."
          />
          <div className="steps-grid">
            {[
              [
                "01",
                "fa-calendar-days",
                "You schedule",
                "Pick your product, days and preferred delivery window.",
              ],
              [
                "02",
                "fa-basket-shopping",
                "We buy or prepare fresh",
                "We purchase fruits for demand and prepare shakes close to delivery.",
              ],
              [
                "03",
                "fa-truck-fast",
                "We deliver",
                "Your fresh essentials arrive in your selected delivery window.",
              ],
            ].map(([n, icon, title, text]) => (
              <div className="step-card" key={n}>
                <span className="step-number">{n}</span>
                <i className={`fa-solid ${icon}`} />
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
          <div className="freshness-banner">
            <i className="fa-solid fa-leaf" />
            <div>
              <strong>Freshness is the system, not a slogan.</strong>
              <p>
                We don’t keep large quantities of your fruits in storage. We
                purchase according to upcoming scheduled demand.
              </p>
            </div>
          </div>
        </div>
        <div className="py-4">
          <CustomerCount count={57} />
        </div>
      </section>

      <section className="section-space">
        <div className="container-xl">
          <SectionTitle
            eyebrow="Made for real routines"
            title="One less thing to remember."
            copy="For gym-goers, IT professionals, working couples and families who want everyday nutrition to just happen."
          />
          <div className="benefit-grid">
            {[
              ["fa-seedling", "Fresh ingredients"],
              ["fa-clock", "Scheduled delivery"],
              ["fa-blender", "Natural nutrition"],
              ["fa-basket-shopping", "Market-bought fruits"],
            ].map(([icon, t]) => (
              <div key={t}>
                <i className={`fa-solid ${icon}`} />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
